import { MenuProps } from 'antd'

import {
  RecurringEventDto,
  RecurringEventDtoBymonth,
  RecurringEventDtoByweekday,
  RecurringEventDtoFreq,
} from '@/api'
import { KBText } from '@/components/atoms'
import { RecurrenceInfo } from '@/components/organisms/KBRecurrenceEditModal/KBRecurrenceEditModal'
import dayjs, { Dayjs, Frequency, formatDate, nameOfDay } from '@wox/dayjs'
import { t } from 'i18next'
import { RRule, rrulestr } from 'rrule'

export enum RecurrenceInterval {
  day = Frequency.DAILY,
  week = Frequency.WEEKLY,
  month = Frequency.MONTHLY,
  year = Frequency.YEARLY,
  noRecurrence = -1,
}

/**
 * 返回rrule里面对应的weekday。rrule的规则是：周天是6，周一是0
 */
export const formatDayjsWeekDayToRRuleWeekDay = (weekDays: number[] = []) => {
  const rruleWeekDay = [6, 0, 1, 2, 3, 4, 5]
  return weekDays.map((i) => rruleWeekDay[i]) as RecurringEventDtoByweekday[]
}

/**
 * 返回dayjs里面对应的weekday。rrule的规则是：周天是6，周一是0
 */
export const formatRRuleWeekDayToDayjsWeekDay = (weekDays: number[] = []) => {
  const dayjsWeekDay = [1, 2, 3, 4, 5, 6, 0]
  return weekDays.map((i) => dayjsWeekDay[i]) as RecurringEventDtoByweekday[]
}

/**
 * 获取当前日期是本月的第x个周，比如说2024年10月29日，是当月的第5个周二，返回5
 */
export const getWeekDayInMonthIndex = (date: Dayjs) => {
  // 计算今天是本月的第几个星期几
  const startOfMonth = dayjs(date).startOf('month')
  const dayOfWeek = date.isoWeekday() // 当前日期是星期几
  let weekNumber = 1

  // 从本月的第一天开始迭代
  let currentDay = startOfMonth
  while (currentDay.isBefore(date, 'day')) {
    // 如果当前日期与目标日期的星期几一致，计数器加 1
    if (currentDay.isoWeekday() === dayOfWeek) {
      weekNumber++
    }
    currentDay = currentDay.add(1, 'day') // 按天遍历
  }

  return weekNumber
}

export const getRecurrenceIntervalDescription = (
  interval?: RecurrenceInterval
): string => {
  switch (interval) {
    case RecurrenceInterval.noRecurrence:
      return t('recurrence.noRecurrence')
    case RecurrenceInterval.day:
      return t('recurrence.day')
    case RecurrenceInterval.week:
      return t('recurrence.week')
    case RecurrenceInterval.month:
      return t('recurrence.month')
    case RecurrenceInterval.year:
      return t('recurrence.year')
    default:
      return t('recurrence.noRecurrence')
  }
}

export const getRecurrenceIntervalOptions = (): MenuProps['items'] => {
  return [
    RecurrenceInterval.noRecurrence,
    RecurrenceInterval.day,
    RecurrenceInterval.week,
    RecurrenceInterval.month,
    RecurrenceInterval.year,
  ].map((interval: RecurrenceInterval) => {
    return {
      key: interval,
      label: <KBText>{getRecurrenceIntervalDescription(interval)}</KBText>,
    }
  })
}

export enum RecurrenceByMonthInterval {
  /**
   * @description 按每月的第几天
   */
  day,
  /**
   * @description 按每月的第几个周几
   */
  week,
}

export enum RecurrenceByYearInterval {
  /**
   * @description 按每年的几月几日
   */
  day,
  /**
   * @description 按每年的几月的第几个周几
   */
  week,
}

/**
 * 将 RecurrenceInfo 转换为 RecurringEventDto
 * @param recurrenceInfo
 * @param timeZone
 * @returns
 */
export const recurrenceInfoToDto = (
  recurrenceInfo?: RecurrenceInfo,
  timeZone?: string
): RecurringEventDto | undefined => {
  let recurringEventDto: RecurringEventDto | undefined = undefined
  if (
    recurrenceInfo &&
    recurrenceInfo?.intervalType !== RecurrenceInterval.noRecurrence
  ) {
    recurringEventDto = {
      interval: recurrenceInfo.interval as number,
      freq: recurrenceInfo.intervalType as RecurringEventDtoFreq,
      until: recurrenceInfo?.endDate?.format('YYYY-MM-DD'),
      tzid: timeZone,
      bymonthday:
        [RecurrenceInterval.month, RecurrenceInterval.year].includes(
          recurrenceInfo?.intervalType
        ) && recurrenceInfo.monthDay
          ? [recurrenceInfo.monthDay]
          : undefined,
      bymonth:
        recurrenceInfo.month !== undefined
          ? [recurrenceInfo.month as RecurringEventDtoBymonth]
          : undefined,
      bysetpos: recurrenceInfo?.monthWeekNumber
        ? [recurrenceInfo?.monthWeekNumber]
        : undefined,
      byweekday:
        recurrenceInfo?.intervalType === RecurrenceInterval.week
          ? formatDayjsWeekDayToRRuleWeekDay(recurrenceInfo?.selectedWeekdays)
          : recurrenceInfo?.monthWeekDay !== undefined
            ? formatDayjsWeekDayToRRuleWeekDay([recurrenceInfo?.monthWeekDay])
            : undefined,
    }
  }
  return recurringEventDto
}

/**
 * 将 RecurringEventDto 转换为 RRULE 字符串
 * @param recurringEventDto
 * @returns RRULE string (without DTSTART)
 */
export const recurringEventDtoToRRule = (
  recurringEventDto?: RecurringEventDto
): string | undefined => {
  if (!recurringEventDto) return undefined

  try {
    // Map weekday numbers: RecurringEventDto uses 0-6 (MO-SU), RRule uses RRule.MO, RRule.TU, etc.
    const weekdayMap = [
      RRule.MO, // 0
      RRule.TU, // 1
      RRule.WE, // 2
      RRule.TH, // 3
      RRule.FR, // 4
      RRule.SA, // 5
      RRule.SU, // 6
    ]

    const options: Partial<ConstructorParameters<typeof RRule>[0]> = {
      freq: recurringEventDto.freq,
      interval: recurringEventDto.interval,
    }

    // Add until if present
    if (recurringEventDto.until) {
      options.until = dayjs(recurringEventDto.until).toDate()
    }

    // Add count if present
    if (recurringEventDto.count) {
      options.count = recurringEventDto.count
    }

    // Add byweekday if present
    if (recurringEventDto.byweekday && recurringEventDto.byweekday.length > 0) {
      options.byweekday = recurringEventDto.byweekday.map(
        (day) => weekdayMap[day]
      )
    }

    // Add bymonthday if present
    if (
      recurringEventDto.bymonthday &&
      recurringEventDto.bymonthday.length > 0
    ) {
      options.bymonthday = recurringEventDto.bymonthday
    }

    // Add bymonth if present
    if (recurringEventDto.bymonth && recurringEventDto.bymonth.length > 0) {
      options.bymonth = recurringEventDto.bymonth
    }

    // Add bysetpos if present
    if (recurringEventDto.bysetpos && recurringEventDto.bysetpos.length > 0) {
      options.bysetpos = recurringEventDto.bysetpos
    }

    // Create RRule object
    const rule = new RRule(options)

    // Convert to string and extract only the RRULE part (without DTSTART)
    const fullString = rule.toString()
    const rruleMatch = fullString.match(/RRULE:(.+)/)

    return rruleMatch ? rruleMatch[1] : undefined
  } catch (error) {
    console.error('Error converting RecurringEventDto to RRULE:', error)
    return undefined
  }
}

/**
 * 根据重复条件获取具体的文案描述
 */
export function getRecurrenceDescription(
  recurrenceInfo?: RecurrenceInfo,
  locale?: string
) {
  if (!recurrenceInfo) return ''

  const { startDate: startTime, intervalType: type } = recurrenceInfo

  if (
    type === RecurrenceInterval.day &&
    recurrenceInfo.endDate &&
    recurrenceInfo.interval
  ) {
    return t('recurrence.byDayDescription', {
      interval: recurrenceInfo.interval,
      endDate: formatDate(recurrenceInfo.endDate.toDate()),
    })
  } else if (
    type === RecurrenceInterval.week &&
    recurrenceInfo.endDate &&
    recurrenceInfo.interval &&
    recurrenceInfo.selectedWeekdays?.length
  ) {
    return t('recurrence.byWeekDescription', {
      interval: recurrenceInfo.interval,
      weekdays: recurrenceInfo.selectedWeekdays
        ?.map((weekday) =>
          nameOfDay(weekday, { weekdayFormat: 'short', locale })
        )
        .join(', '),
      endDate: formatDate(recurrenceInfo.endDate.toDate()),
    })
  } else if (type === RecurrenceInterval.month) {
    if (
      recurrenceInfo.monthRecurrenceByMonthInterval ===
      RecurrenceByMonthInterval.day
    ) {
      return t('recurrence.byMonthDayDescription', {
        interval: recurrenceInfo.interval,
        day: startTime.date(),
        endDate: formatDate(recurrenceInfo.endDate.toDate()),
      })
    } else {
      return t('recurrence.byMonthWeekDescription', {
        interval: recurrenceInfo.interval,
        week: getWeekDayInMonthIndex(startTime),
        weekday: nameOfDay(startTime.day(), {
          weekdayFormat: 'short',
          locale,
        }),
        endDate: formatDate(recurrenceInfo.endDate.toDate()),
      })
    }
  } else if (type === RecurrenceInterval.year) {
    if (
      recurrenceInfo.yearRecurrenceByYearInterval ===
      RecurrenceByYearInterval.day
    ) {
      return t('recurrence.byYearMonthDayDescription', {
        month: startTime.month() + 1,
        day: startTime.date(),
        endDate: formatDate(recurrenceInfo.endDate.toDate()),
      })
    } else {
      return t('recurrence.byYearMonthWeekDescription', {
        month: startTime.month() + 1,
        week: getWeekDayInMonthIndex(startTime),
        weekday: nameOfDay(startTime.day(), {
          weekdayFormat: 'short',
          locale,
        }),
        endDate: formatDate(recurrenceInfo.endDate.toDate()),
      })
    }
  } else {
    return ''
  }
}

/**
 * 根据RRule字符串组装成RecurrenceInfo格式
 */
export const fillRecurrenceInfoByRrule = (
  rruleString?: string,
  startDate?: string
): RecurrenceInfo | undefined => {
  if (!rruleString) return
  const repeatInfo = rrulestr(rruleString).options

  const { freq } = repeatInfo

  const common = {
    interval: repeatInfo.interval,
    startDate: dayjs(startDate),
    endDate: dayjs(repeatInfo.until),
    intervalType: repeatInfo.freq as number,
  }

  if (freq === Frequency.DAILY) {
    // 按天
    return {
      ...common,
    }
  } else if (freq === Frequency.WEEKLY) {
    // 按周
    return {
      ...common,
      selectedWeekdays: formatRRuleWeekDayToDayjsWeekDay(repeatInfo.byweekday),
    }
  } else if (freq === Frequency.MONTHLY) {
    if (repeatInfo.bymonthday?.length) {
      // 按月-第几天
      return {
        ...common,
        monthRecurrenceByMonthInterval: RecurrenceByMonthInterval.day,
        monthDay: repeatInfo.bymonthday?.[0],
      }
    } else {
      // 按月-第几周的周几
      return {
        ...common,
        monthRecurrenceByMonthInterval: RecurrenceByMonthInterval.week,
        monthWeekNumber: repeatInfo.bysetpos?.[0],
        monthWeekDay: formatRRuleWeekDayToDayjsWeekDay(
          repeatInfo.byweekday
        )?.[0],
      }
    }
  } else if (freq === Frequency.YEARLY) {
    if (repeatInfo.bymonthday?.length) {
      // 按年-几月几日
      return {
        ...common,
        yearRecurrenceByYearInterval: RecurrenceByYearInterval.day,
        month: repeatInfo.bymonth?.[0],
        monthDay: repeatInfo.bymonthday?.[0],
      }
    } else {
      // 按年-几月第几个周几
      return {
        ...common,
        yearRecurrenceByYearInterval: RecurrenceByYearInterval.week,
        month: repeatInfo.bymonth?.[0],
        monthWeekNumber: repeatInfo.bysetpos?.[0],
        monthWeekDay: repeatInfo.byweekday?.[0],
      }
    }
  }
}
